import { z } from 'zod';

// ─── Shared enums (mirrored from backend-core for client use) ────────────────

export const AnswerTypeEnum = z.enum([
  'free_text',
  'single_select',
  'multi_select',
  'yes_no',
  'numeric',
  'numeric_range',
  'ranked_list',
  'likert',
]);

export type AnswerType = z.infer<typeof AnswerTypeEnum>;

export const AnswerTypeLabels: Record<AnswerType, string> = {
  free_text: 'Free Text',
  single_select: 'Single Select',
  multi_select: 'Multi Select',
  yes_no: 'Yes/No',
  numeric: 'Numeric',
  numeric_range: 'Numeric Range',
  ranked_list: 'Ranked List',
  likert: 'Likert Scale',
};

export const ConfidenceEnum = z.enum(['high', 'medium', 'low', 'not_found', 'error']);

export type Confidence = z.infer<typeof ConfidenceEnum>;

export const SourceSnippetSchema = z.object({
  sourceId: z.string(),
  sentence: z.string(),
  start: z.number().nullish(),
  end: z.number().nullish(),
});

export type SourceSnippet = z.infer<typeof SourceSnippetSchema>;

export const StoredSourceSchema = z.object({
  sourceId: z.string(),
  sourceType: z.literal('transcript'),
  transcript: z.object({
    id: z.number(),
    title: z.string(),
    publishDate: z.coerce.date().nullable().optional(),
    call: z.object({ id: z.number() }).nullish(),
    project: z.object({ id: z.number() }).nullish(),
  }),
  content: z.string(),
  ordinal: z.number(),
  start: z.number(),
  end: z.number(),
  tags: z.array(z.string()).optional(),
  url: z.string(),
  score: z.number(),
  rerankScore: z.number(),
});

export type StoredSource = z.infer<typeof StoredSourceSchema>;

export const SourceSnippetsDataSchema = z.object({
  snippets: z.array(SourceSnippetSchema),
  sources: z.array(StoredSourceSchema),
});

export type SourceSnippetsData = z.infer<typeof SourceSnippetsDataSchema>;

// ─── Answer data schemas (discriminated union by answerType) ─────────────────

const answerMetadata = {
  confidence: ConfidenceEnum,
  confidenceRationale: z.string().nullish(),
  notFoundReason: z.string().nullish(),
  sourceSnippets: SourceSnippetsDataSchema,
};

export const ExtractedAnswerDataSchema = z.discriminatedUnion('answerType', [
  z.object({ ...answerMetadata, answerType: z.literal('free_text'), value: z.string().nullish() }),
  z.object({
    ...answerMetadata,
    answerType: z.literal('single_select'),
    value: z.string().nullish(),
    otherDetail: z.string().nullish(),
  }),
  z.object({
    ...answerMetadata,
    answerType: z.literal('multi_select'),
    values: z.array(z.string()),
    otherDetail: z.string().nullish(),
  }),
  z.object({
    ...answerMetadata,
    answerType: z.literal('yes_no'),
    value: z.boolean().nullish(),
    rationale: z.string().nullish(),
  }),
  z.object({
    ...answerMetadata,
    answerType: z.literal('numeric'),
    value: z.number().nullish(),
    unit: z.string().nullish(),
  }),
  z.object({ ...answerMetadata, answerType: z.literal('numeric_range'), value: z.string().nullish() }),
  z.object({
    ...answerMetadata,
    answerType: z.literal('ranked_list'),
    rankedItems: z.array(z.object({ rank: z.number().int(), label: z.string() })),
  }),
  z.object({
    ...answerMetadata,
    answerType: z.literal('likert'),
    value: z.string().nullish(),
    rationale: z.string().nullish(),
  }),
]);

export type ExtractedAnswerData =
  | {
      confidence: Confidence;
      confidenceRationale?: string | null;
      notFoundReason?: string | null;
      sourceSnippets: SourceSnippetsData;
      answerType: 'free_text';
      value?: string | null;
    }
  | {
      confidence: Confidence;
      confidenceRationale?: string | null;
      notFoundReason?: string | null;
      sourceSnippets: SourceSnippetsData;
      answerType: 'single_select';
      value?: string | null;
      otherDetail?: string | null;
    }
  | {
      confidence: Confidence;
      confidenceRationale?: string | null;
      notFoundReason?: string | null;
      sourceSnippets: SourceSnippetsData;
      answerType: 'multi_select';
      values: string[];
      otherDetail?: string | null;
    }
  | {
      confidence: Confidence;
      confidenceRationale?: string | null;
      notFoundReason?: string | null;
      sourceSnippets: SourceSnippetsData;
      answerType: 'yes_no';
      value?: boolean | null;
      rationale?: string | null;
    }
  | {
      confidence: Confidence;
      confidenceRationale?: string | null;
      notFoundReason?: string | null;
      sourceSnippets: SourceSnippetsData;
      answerType: 'numeric';
      value?: number | null;
      unit?: string | null;
    }
  | {
      confidence: Confidence;
      confidenceRationale?: string | null;
      notFoundReason?: string | null;
      sourceSnippets: SourceSnippetsData;
      answerType: 'numeric_range';
      value?: string | null;
    }
  | {
      confidence: Confidence;
      confidenceRationale?: string | null;
      notFoundReason?: string | null;
      sourceSnippets: SourceSnippetsData;
      answerType: 'ranked_list';
      rankedItems: { rank: number; label: string }[];
    }
  | {
      confidence: Confidence;
      confidenceRationale?: string | null;
      notFoundReason?: string | null;
      sourceSnippets: SourceSnippetsData;
      answerType: 'likert';
      value?: string | null;
      rationale?: string | null;
    };

// ─── Option item schema (used for select/ranked/likert/range types) ─────────

export const QuestionOptionSchema = z.object({
  id: z.string(),
  label: z.string(),
});

export type QuestionOption = z.infer<typeof QuestionOptionSchema>;

// ─── Extraction question config (discriminated union by answerType) ──────────

const questionBase = {
  questionText: z.string(),
  label: z.string(),
  bucket: z.string().nullish(),
  searchQuery: z.string().nullish(),
  keywords: z.array(z.string()).nullish(),
  extractionGuidance: z.string().nullish(),
  sectionContext: z.string().nullish(),
};

export const ExtractionQuestionSchema = z.discriminatedUnion('answerType', [
  z.object({ ...questionBase, answerType: z.literal('free_text') }),
  z.object({
    ...questionBase,
    answerType: z.literal('single_select'),
    options: z.array(QuestionOptionSchema),
  }),
  z.object({
    ...questionBase,
    answerType: z.literal('multi_select'),
    options: z.array(QuestionOptionSchema),
  }),
  z.object({ ...questionBase, answerType: z.literal('yes_no') }),
  z.object({ ...questionBase, answerType: z.literal('numeric'), unit: z.string().nullish() }),
  z.object({
    ...questionBase,
    answerType: z.literal('numeric_range'),
    ranges: z.array(QuestionOptionSchema),
  }),
  z.object({ ...questionBase, answerType: z.literal('ranked_list'), options: z.array(QuestionOptionSchema) }),
  z.object({ ...questionBase, answerType: z.literal('likert'), scale: z.array(QuestionOptionSchema) }),
]);

export type ExtractionQuestion =
  | {
      questionText: string;
      label: string;
      bucket?: string | null;
      searchQuery?: string | null;
      keywords?: string[] | null;
      extractionGuidance?: string | null;
      sectionContext?: string | null;
      answerType: 'free_text';
    }
  | {
      questionText: string;
      label: string;
      bucket?: string | null;
      searchQuery?: string | null;
      keywords?: string[] | null;
      extractionGuidance?: string | null;
      sectionContext?: string | null;
      answerType: 'single_select';
      options: QuestionOption[];
    }
  | {
      questionText: string;
      label: string;
      bucket?: string | null;
      searchQuery?: string | null;
      keywords?: string[] | null;
      extractionGuidance?: string | null;
      sectionContext?: string | null;
      answerType: 'multi_select';
      options: QuestionOption[];
    }
  | {
      questionText: string;
      label: string;
      bucket?: string | null;
      searchQuery?: string | null;
      keywords?: string[] | null;
      extractionGuidance?: string | null;
      sectionContext?: string | null;
      answerType: 'yes_no';
    }
  | {
      questionText: string;
      label: string;
      bucket?: string | null;
      searchQuery?: string | null;
      keywords?: string[] | null;
      extractionGuidance?: string | null;
      sectionContext?: string | null;
      answerType: 'numeric';
      unit?: string | null;
    }
  | {
      questionText: string;
      label: string;
      bucket?: string | null;
      searchQuery?: string | null;
      keywords?: string[] | null;
      extractionGuidance?: string | null;
      sectionContext?: string | null;
      answerType: 'numeric_range';
      ranges: QuestionOption[];
    }
  | {
      questionText: string;
      label: string;
      bucket?: string | null;
      searchQuery?: string | null;
      keywords?: string[] | null;
      extractionGuidance?: string | null;
      sectionContext?: string | null;
      answerType: 'ranked_list';
      options: QuestionOption[];
    }
  | {
      questionText: string;
      label: string;
      bucket?: string | null;
      searchQuery?: string | null;
      keywords?: string[] | null;
      extractionGuidance?: string | null;
      sectionContext?: string | null;
      answerType: 'likert';
      scale: QuestionOption[];
    };

// ─── API namespace ───────────────────────────────────────────────────────────

export namespace ExtractedAnswersAPI {
  export const conferenceTagSummarySchema = z.object({
    id: z.number(),
    name: z.string(),
    color: z.string(),
    ordinal: z.number(),
  });

  export type ConferenceTagSummary = z.infer<typeof conferenceTagSummarySchema>;

  export namespace GetExtractedAnswers {
    export const requestParamsSchema = z.object({
      transcriptId: z.coerce.number(),
    });

    export type Request = { transcriptId: number };

    const questionSummarySchema = z.object({
      id: z.number(),
      answerConfig: ExtractionQuestionSchema,
    });

    const extractedAnswerItemSchema = z.object({
      id: z.number(),
      questionId: z.number(),
      question: questionSummarySchema,
      answerData: ExtractedAnswerDataSchema.nullable(),
      confidence: ConfidenceEnum,
      sources: z.unknown().nullable(),
      markdownSummary: z.string().nullable(),
      errorMessage: z.string().nullish(),
      startedOn: z.coerce.date(),
      completedOn: z.coerce.date().nullable(),
      isRefreshing: z.boolean(),
      tag: conferenceTagSummarySchema.nullable(),
      questionOrdinal: z.number(),
      langfuseTraceUrl: z.string().nullish(),
    });

    export const responseBodySchema = z.object({
      items: z.array(extractedAnswerItemSchema),
    });

    export type ExtractedAnswerItem = z.infer<typeof extractedAnswerItemSchema>;
    export type Response = z.infer<typeof responseBodySchema>;
  }

  export namespace GetTagExtractionQuestions {
    export const requestParamsSchema = z.object({
      projectId: z.coerce.number(),
      tagId: z.coerce.number(),
    });

    export type Request = { projectId: number; tagId: number };

    const tagExtractionQuestionItemSchema = z.object({
      id: z.number(),
      identifier: z.string(),
      question: ExtractionQuestionSchema,
      ordinal: z.number(),
    });

    export const responseBodySchema = z.object({
      items: z.array(tagExtractionQuestionItemSchema),
    });

    export type TagExtractionQuestionItem = z.infer<typeof tagExtractionQuestionItemSchema>;
    export type Response = z.infer<typeof responseBodySchema>;
  }

  export namespace UpdateTagExtractionQuestions {
    export const requestParamsSchema = z.object({
      projectId: z.coerce.number(),
      tagId: z.coerce.number(),
    });

    export const requestBodySchema = z.object({
      questions: z.array(
        z.object({
          identifier: z.string(),
          question: ExtractionQuestionSchema,
          ordinal: z.number(),
        })
      ),
    });

    export type Request = {
      projectId: number;
      tagId: number;
      questions: Array<{
        identifier: string;
        question: ExtractionQuestion;
        ordinal: number;
      }>;
    };

    export const responseBodySchema = z.object({
      items: z.array(
        z.object({
          id: z.number(),
          identifier: z.string(),
          question: ExtractionQuestionSchema,
          ordinal: z.number(),
        })
      ),
    });

    export type Response = z.infer<typeof responseBodySchema>;
  }

  export namespace RefreshExtractedAnswers {
    export const requestParamsSchema = z.object({
      transcriptId: z.coerce.number(),
    });

    export const requestBodySchema = z.object({
      questionIds: z.array(z.number()).optional(),
    });

    export type Request = { transcriptId: number; questionIds?: number[] };

    export const responseBodySchema = z.object({
      queued: z.number(),
    });

    export type Response = z.infer<typeof responseBodySchema>;
  }
}
